import { useState, useEffect, useCallback } from "react";

interface CookieOptions {
	expires?: number; // days
	path?: string;
	domain?: string;
	secure?: boolean;
	sameSite?: "strict" | "lax" | "none";
}

const COOKIE_NAME = "pyblaze_code_content";
const COOKIE_OPTIONS: CookieOptions = {
	expires: 30, // 30 days for code persistence
	path: "/",
	sameSite: "lax",
};

// Cookie utility functions
const setCookie = (
	name: string,
	value: string,
	options: CookieOptions = {},
) => {
	let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

	if (options.expires) {
		const date = new Date();
		date.setTime(date.getTime() + options.expires * 24 * 60 * 60 * 1000);
		cookieString += `; expires=${date.toUTCString()}`;
	}

	if (options.path) {
		cookieString += `; path=${options.path}`;
	}

	if (options.domain) {
		cookieString += `; domain=${options.domain}`;
	}

	if (options.secure) {
		cookieString += `; secure`;
	}

	if (options.sameSite) {
		cookieString += `; samesite=${options.sameSite}`;
	}

	document.cookie = cookieString;
};

const getCookie = (name: string): string | null => {
	const nameEQ = encodeURIComponent(name) + "=";
	const ca = document.cookie.split(";");

	for (let i = 0; i < ca.length; i++) {
		let c = ca[i];
		while (c.charAt(0) === " ") c = c.substring(1, c.length);
		if (c.indexOf(nameEQ) === 0) {
			return decodeURIComponent(c.substring(nameEQ.length, c.length));
		}
	}

	return null;
};

const deleteCookie = (name: string, path: string = "/") => {
	document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
};

export const useCodePersistence = (initialCode?: string) => {
	const [code, setCodeState] = useState<string>(() => {
		if (typeof window === "undefined") {
			return initialCode || "";
		}

		// If initialCode is provided (from shared URL), use it instead of cookie
		if (initialCode) {
			return initialCode;
		}

		try {
			const savedCode = getCookie(COOKIE_NAME);
			if (savedCode && savedCode.trim()) {
				return savedCode;
			}
		} catch (error) {
			console.warn("Failed to load code from cookie:", error);
		}

		// Return empty string instead of default template
		return "";
	});

	// Save code to cookie whenever it changes (with debouncing to avoid excessive writes)
	useEffect(() => {
		if (typeof window !== "undefined") {
			const timeoutId = setTimeout(() => {
				try {
					if (code.trim() === "") {
						// If code is empty/cleared, delete the cookie entirely
						deleteCookie(COOKIE_NAME);
					} else {
						// Save non-empty code to cookie
						setCookie(COOKIE_NAME, code, COOKIE_OPTIONS);
					}
				} catch (error) {
					console.error("Failed to save code to cookie:", error);
				}
			}, 1000); // Debounce for 1 second

			return () => clearTimeout(timeoutId);
		}
	}, [code]);

	const setCode = useCallback((newCode: string) => {
		setCodeState(newCode);
	}, []);

	const resetCode = useCallback(() => {
		setCodeState("");
		deleteCookie(COOKIE_NAME);
	}, []);

	const clearSavedCode = useCallback(() => {
		deleteCookie(COOKIE_NAME);
	}, []);

	const clearCode = useCallback(() => {
		setCodeState("");
	}, []);

	return {
		code,
		setCode,
		resetCode,
		clearCode,
		clearSavedCode,
		hasUnsavedChanges: code !== "",
	};
};
